
import { useState } from "react";
import { PricingCard } from "./PricingCard";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";

const plans = {
  basic: {
    title: "Small Business Plan",
    description: "Perfect for small teams",
    price: "$75",
    features: [
      { text: "Advanced data analysis", included: true },
      { text: "Up to 5,000 records/month", included: true },
      { text: "Email support", included: true },
      { text: "Basic integrations", included: true },
      { text: "Custom dashboards", included: false },
    ],
    planId: "154077",
  },
  standard: {
    title: "Mid-Size Business Plan",
    description: "Great for growing teams",
    price: "$150",
    features: [
      { text: "Advanced data analysis", included: true },
      { text: "Up to 25,000 records/month", included: true },
      { text: "Priority support", included: true },
      { text: "All integrations", included: true },
      { text: "Custom dashboards", included: true },
    ],
    popular: true,
    planId: "154075",
  },
  premium: {
    title: "Enterprise Plan",
    description: "For large organizations",
    price: "$200",
    features: [
      { text: "Enterprise data analysis", included: true },
      { text: "Unlimited records", included: true },
      { text: "24/7 dedicated support", included: true },
      { text: "All integrations + custom", included: true },
      { text: "White-label solutions", included: true },
    ],
    planId: "154076",
  },
};

interface PricingSectionProps {
  businessType?: string | null;
}

export const PricingSection = ({ businessType }: PricingSectionProps) => {
  const [loading, setLoading] = useState(false);

  // Filter plans based on business type
  const getRelevantPlans = () => {
    if (!businessType) return plans;

    switch (businessType.toLowerCase()) {
      case 'startup':
      case 'small':
        return { basic: plans.basic, standard: plans.standard };
      case 'midsize':
      case 'medium': 
        return { standard: plans.standard, premium: plans.premium };
      case 'enterprise':
      case 'large':
        return { premium: plans.premium };
      default:
        return plans;
    }
  };

  const relevantPlans = getRelevantPlans();

  const handleSelectPlan = async (planType: keyof typeof plans) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Please sign in to continue");
        return;
      }

      setLoading(true);
      const plan = plans[planType];
      
      // Convert price string to number (remove $ and + symbols)
      const priceString = plan.price.replace(/[$+,]/g, '');
      const amount = parseInt(priceString) || 0;

      try {
        // Create a payment record for tracking
        const { error: paymentError } = await supabase
          .from('payments')
          .insert({
            user_id: user.id,
            transaction_ref: `subscription_${planType}_${Date.now()}`,
            amount: amount,
            currency: "USD",
            payment_type: "subscription",
            status: "pending",
            plan_id: planType,
            metadata: {
              plan: planType,
              plan_name: plan.title,
              flutterwave_plan_id: plan.planId,
            },
          });

        if (paymentError) {
          console.error("Error creating payment record:", paymentError);
          toast.error("Failed to initialize subscription. Please try again.");
          return;
        }

        // Redirect to Flutterwave subscription page with plan ID
        const flutterwaveUrl = `https://flutterwave.com/pay/${plan.planId}`;
        window.location.href = flutterwaveUrl;
      } catch (error) {
        console.error("Error:", error);
        toast.error("Unable to process subscription. Please try again or contact support.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const planEntries = Object.entries(relevantPlans) as Array<[keyof typeof plans, typeof plans[keyof typeof plans]]>;
  const gridCols = planEntries.length === 1 ? "grid-cols-1 max-w-md" : 
                   planEntries.length === 2 ? "grid-cols-1 md:grid-cols-2 max-w-4xl" : 
                   "grid-cols-1 md:grid-cols-3 max-w-6xl";

  return (
    <section className="py-12">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold">
          {businessType ? `Plans for ${businessType.charAt(0).toUpperCase() + businessType.slice(1)} Business` : 'Choose Your Monthly Plan'}
        </h2>
        <p className="text-muted-foreground">
          {businessType 
            ? `Recommended subscription plans tailored for your business size.`
            : 'Select the subscription that best fits your business needs.'
          }
        </p>
      </div>
      <div className={`grid ${gridCols} gap-8 mx-auto px-4`}>
        {planEntries.map(([key, plan]) => (
          <PricingCard
            key={key}
            {...plan}
            onSelect={() => handleSelectPlan(key)}
          />
        ))}
      </div>
    </section>
  );
};
